/**
 * Textarea field component
 * Multi-line text input with optional system default reset
 */

import React, { useState } from 'react';
import { RotateCcw, Loader2 } from 'lucide-react';
import { FieldSchema, ValidationResult, getHelpText } from '@/config/fieldConfig';
import { fetchSystemDefault } from '@/lib/api/systemDefaults';
import { useI18n } from '@/i18n';

export interface TextareaFieldProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
}

export const TextareaField: React.FC<TextareaFieldProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
}) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const label = typeof schema.label === 'function' ? schema.label() : schema.label;
  const placeholder = typeof schema.placeholder === 'function' ? schema.placeholder() : schema.placeholder;
  const help = getHelpText(schema.help, value);
  const rows = schema.rows || 3;

  const borderClass = error && !error.isWarning
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : error?.isWarning
    ? 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500'
    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

  // Handle reset to system default
  const handleResetToSystemDefault = async () => {
    setIsLoading(true);
    setResetError(null);

    try {
      const result = await fetchSystemDefault(name);
      if (result.success && result.value !== undefined) {
        // For JSON fields, stringify the value
        const newValue = typeof result.value === 'object'
          ? JSON.stringify(result.value, null, 2)
          : String(result.value);
        onChange(newValue);
      } else {
        setResetError(result.error || 'Failed to load system default');
      }
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to load system default');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="field-wrapper">
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={`field-${name}`} className="block text-sm font-medium text-gray-700">
          {label}
          {schema.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {schema.hasResetButton && !disabled && !schema.readonly && (
          <button
            type="button"
            onClick={handleResetToSystemDefault}
            disabled={isLoading}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 transition-colors"
            title={t('actions.resetToSystemDefault')}
          >
            {isLoading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RotateCcw className="w-3 h-3" />
            )}
            {t('actions.resetToSystemDefault')}
          </button>
        )}
      </div>

      <textarea
        id={`field-${name}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={schema.readonly}
        rows={rows}
        className={`
          w-full px-3 py-2 rounded-lg border
          focus:outline-none focus:ring-2
          disabled:bg-gray-100 disabled:cursor-not-allowed
          read-only:bg-gray-50 read-only:cursor-not-allowed
          resize-y
          ${borderClass}
        `}
      />

      {error && (
        <div className={`text-xs mt-1 ${error.isWarning ? 'text-yellow-600' : 'text-red-600'}`}>
          {error.message}
        </div>
      )}

      {resetError && (
        <div className="text-xs mt-1 text-red-600">
          {resetError}
        </div>
      )}

      {help && !error && !resetError && (
        <div className="text-xs text-gray-500 mt-1">
          {help}
        </div>
      )}
    </div>
  );
};

export default TextareaField;
